import _ from 'lodash';
import QueryString from 'query-string';
import { Flex } from '../base/layout-components';
import AppRouter from 'common/AppRouter';
import RelativeCircle from '../RelativeCircle';
import { SkeletonRow } from '../Skeleton';
import { metricNames } from '@linearb/linjs-common';
import { matchContributorMetricsToContributor } from 'common/services/contributors.service';
import { pluralize } from 'common/utils/utils';
import { StyledWidgetHeader } from 'components/base/common-styles';
import {
  LBArrowCornerRightUpIcon,
  LBButton,
  LBTooltip,
  LBTooltipContent,
  LBTooltipProvider,
  LBTooltipTrigger
} from '@linearb/buzz';
import { Link } from 'react-router-dom';

const DEFAULT_METRIC_VALUE = 0;

const MIN_REVIEWS_FOR_TOP_REVIEWER = 0;

const MIN_ACTIVE_COUNT_FOR_WIP = 6;

const MIN_ACTIVITY_DAYS_FOR_BURNOUT = 6;

const MIN_RATIO_OF_ACTIVITY_DAYS_TO_ITERATION_DAYS = 0.9;

export const PlaceHolderCircle = ({ size }) => (
  <RelativeCircle
    displayValue={undefined}
    value={size}
    valuesConfig={[0.5, 1.5, 2.5, 4]}
    color='catskillWhite'
    opacity={1}
  />
);
export const PlaceHolderRow = () => <SkeletonRow />;

export const NameColumnHeaderComponent = ({ contributorsMetrics }) => {
  const numberOfTeamMembers = contributorsMetrics.length || '';

  return (
    <Flex items='center' justify='space-between'>
      <Flex items='center'>
        <StyledWidgetHeader style={{ padding: '1rem' }}>
          {pluralize(numberOfTeamMembers, 'Team Member')}
        </StyledWidgetHeader>
      </Flex>
    </Flex>
  );
};

export const TeamActivityLink = ({ selectedTeam, currentIteration }) => {
  return (
    <div className='justify-end items-center'>
      <LBTooltipProvider>
        <LBTooltip>
          <LBTooltipTrigger asChild>
            <LBButton size='icon' variant='ghost' asChild>
              <Link
                className='text-[unset]'
                to={{
                  pathname: AppRouter.INDIVIDUALS.path,
                  search: QueryString.stringify({
                    teams: _.get(selectedTeam, 'id'),
                    time: `${_.get(currentIteration, 'start_date')},${_.get(
                      currentIteration,
                      'end_date'
                    )}`
                  })
                }}
              >
                <LBArrowCornerRightUpIcon />
              </Link>
            </LBButton>
          </LBTooltipTrigger>
          <LBTooltipContent>More details on team activity</LBTooltipContent>
        </LBTooltip>
      </LBTooltipProvider>
    </div>
  );
};

export const getContributerMetricsParams = ({ contributorMetric }) => {
  const reviews =
    _.get(contributorMetric, metricNames.REVIEWS, DEFAULT_METRIC_VALUE) ||
    DEFAULT_METRIC_VALUE;

  const activeCount =
    _.get(
      contributorMetric,
      metricNames.ACTIVE_BRANCHES,
      DEFAULT_METRIC_VALUE
    ) || DEFAULT_METRIC_VALUE;

  const activityDays =
    _.get(contributorMetric, metricNames.ACTIVE_DAYS, DEFAULT_METRIC_VALUE) ||
    DEFAULT_METRIC_VALUE;

  return {
    reviews,
    activeCount,
    activityDays
  };
};

export const getTopReviewer = ({ contributors, metrics }) => {
  const reviwes = matchContributorMetricsToContributor(
    contributors,
    metrics
  ).map(contributorMetric => {
    const reviwes =
      _.get(contributorMetric, metricNames.REVIEWS, DEFAULT_METRIC_VALUE) ||
      DEFAULT_METRIC_VALUE;

    const contributorId = _.get(contributorMetric, 'contributor_id');
    return {
      contributorId,
      reviwes
    };
  });
  // @ts-ignore
  return _.maxBy(reviwes, review => review.reviwes);
};

// getAchievementScore returns an AchievementScore,
//  so that the array of metrics of contributers could be sorted in this order:
// top reviwer first, all wip second, all burnout third, the rest with no achievments last
const getAchievementScore = (contributorMetric, topReviewer, iterationDays) => {
  const { reviews, activeCount, activityDays } = getContributerMetricsParams({
    contributorMetric
  });
  const isTopReviewer =
    topReviewer.contributorId === contributorMetric.contributor_id &&
    reviews > MIN_REVIEWS_FOR_TOP_REVIEWER;
  const isWipOverload = activeCount >= MIN_ACTIVE_COUNT_FOR_WIP;
  const isBurnout =
    activityDays > MIN_ACTIVITY_DAYS_FOR_BURNOUT &&
    activityDays / iterationDays >=
      MIN_RATIO_OF_ACTIVITY_DAYS_TO_ITERATION_DAYS;

  if (isTopReviewer) {
    return 1;
  } else if (isWipOverload) {
    return 2;
  } else if (isBurnout) {
    return 3;
  } else {
    return 4;
  }
};

export const sortContributorsMetrics = (
  contributorsMetrics,
  topReviewer,
  iterationDays
) => {
  const contributorsMetricsWithAchievements = contributorsMetrics.map(
    contributorMetric => {
      contributorMetric.achievement = getAchievementScore(
        contributorMetric,
        topReviewer,
        iterationDays
      );
      return contributorMetric;
    }
  );

  return contributorsMetricsWithAchievements.sort(
    (contributorMetrics1, contributorMetrics2) =>
      contributorMetrics1.achievement - contributorMetrics2.achievement
  );
};
